import { Dispatch, SetStateAction } from 'react';

import {
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { FormStateErrors } from '@components/form';
import { showError, showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import { useInvalidateAuthentication, useLogout } from 'api/authentication/hooks';
import { useCompleteOnboardingStep } from 'api/company/hooks';
import { handleError, handlePossibleFieldError } from 'api/query-client';

import {
  canSetPassword,
  createAndInviteUsers,
  deleteUser,
  editMyDetails,
  editUser,
  inviteUser,
  loadMyDetails,
  loadUsers,
  resendSignupConfirmation,
  resetPassword,
  setCompanyUserInfo,
  setPassword,
  signupCompany,
  signupCompanyConfirm,
  signupCompanyConfirmUuid,
  signUpUser,
  updatePassword,
  UserInvite,
} from './requests';

const useInvalidateUsers = () => {
  const queryClient = useQueryClient();
  const invalidateUsers = () => {
    queryClient.invalidateQueries(['users']);
  };
  const invalidateUser = (id: string) => {
    queryClient.invalidateQueries(['user', id]);
  };
  return { invalidateUsers, invalidateUser };
};

export const useUsers = (queryString?: string) => {
  return useQuery({
    queryKey: ['users', queryString],
    queryFn: () => loadUsers(queryString),
  });
};

export const useActiveUsers = () => {
  return useQuery({
    queryKey: ['users', 'active'],
    queryFn: () => loadUsers('?ignore_invited=true'),
  });
};

export const useActiveWorkspaceUsers = (workspaceId: string) => {
  return useQuery({
    queryKey: ['users', 'workspace', workspaceId],
    queryFn: () => loadUsers(`?ignore_invited=true&workspace=${workspaceId}`),
    enabled: Boolean(workspaceId),
  });
};

const handleEmailError = (
  data: any,
  defaultMessage: string,
  setEmailError?: (errors: string[]) => void
) => {
  handleError(() => {
    if (data?.response?.data?.email) {
      setEmailError?.(data.response.data.email);
    } else {
      showError(defaultMessage);
    }
  });
};

type UserError = AxiosError<{ email: string[] }>;

export const useEditUser = (
  id: string,
  setEmailError: (errors: string[]) => void,
  options?: UseMutationOptions<unknown, UserError, FormData>
) => {
  const { t } = useScopedTranslate(locale);

  const { invalidateUsers, invalidateUser } = useInvalidateUsers();
  return useMutation({
    ...options,
    mutationFn: (data) => editUser(id, data),
    onSuccess: (...data) => {
      invalidateUsers();
      invalidateUser(id);
      showSuccess(t('userEdited'));
      options?.onSuccess?.(...data);
    },
    onError: (data) => {
      handleEmailError(data, t('userNotEdited'), setEmailError);
    },
  });
};

type DeleteUserProps = {
  id: string;
  onSuccess?: () => void;
  toasterSuccessMessage?: string;
};
export function useDeleteUser(props: DeleteUserProps) {
  const { t } = useScopedTranslate(locale);
  const { invalidateUsers } = useInvalidateUsers();

  return useMutation(() => deleteUser(props.id), {
    onSuccess: () => {
      showSuccess(props.toasterSuccessMessage ?? t('userDeleted'));
      invalidateUsers();
      props.onSuccess?.();
    },
    onError: () => {
      handleError(t('userNotDeleted'));
    },
  });
}

export const useMyDetails = () => {
  return useQuery({
    queryKey: ['my-details'],
    queryFn: () => loadMyDetails(),
  });
};

export const useEditMyDetails = (
  setEmailError?: (errors: string[]) => void,
  options?: UseMutationOptions<unknown, UserError, FormData>
) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (data) => editMyDetails(data),
    onSuccess: (...data) => {
      queryClient.invalidateQueries(['my-details']);
      invalidateCurrentUser();
      options?.onSuccess?.(...data);
    },
    onError: (data) => {
      handleEmailError(data, t('userNotEdited'), setEmailError);
    },
  });
};

export const useCanSetPassword = (
  {
    uuid,
    passwordUuid,
  }: {
    uuid: string;
    passwordUuid: string;
  },
  options?: UseQueryOptions<boolean, unknown> & {
    onSettled: Dispatch<SetStateAction<boolean>>;
  }
) => {
  return useQuery({
    queryKey: ['can-set-password'],
    queryFn: () => canSetPassword(uuid, passwordUuid),
    ...options,
  });
};

export type SetPassword = {
  uuid: string;
  passwordUuid: string;
  password1: string;
  password2: string;
};

export const useSetPassword = (options?: UseMutationOptions<unknown, unknown, SetPassword>) => {
  const { t } = useScopedTranslate(locale);

  return useMutation({
    ...options,
    mutationFn: (data) => setPassword(data),
    onSuccess: (...data) => {
      options?.onSuccess?.(...data);
    },
    onError: () => {
      handleError(t('passwordNotSet'));
    },
  });
};

export type SignUpUserData = {
  uuid: string;
  passwordUuid: string;
  fullName: string;
  password: string;
  phone?: string;
};

export const useSignUpUser = () => {
  const { t } = useScopedTranslate(locale);
  const { invalidateCurrentUser } = useInvalidateAuthentication();

  return useMutation({
    mutationFn: (data: SignUpUserData) => signUpUser(data),
    onSuccess: () => {
      invalidateCurrentUser();
    },
    onError: () => {
      handleError(t('signUpFailed'));
    },
  });
};

export const useSignupCompany = ({ onSuccess }: { onSuccess: () => void }) => {
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationFn: signupCompany,
    onSuccess,
    onError: (error) => {
      handlePossibleFieldError(error, t('signUpFailed'));
    },
  });
};

export const useResendSignupConfirmation = () => {
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationFn: resendSignupConfirmation,
    onError: (error) => {
      handlePossibleFieldError(error, t('resendFailed'));
    },
  });
};

export const useSignupCompanyConfirm = ({ onSuccess }: { onSuccess: () => void }) => {
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationFn: signupCompanyConfirm,
    onSuccess,
    onError: (error) => {
      handlePossibleFieldError(error, t('signUpFailed'));
    },
  });
};

export const useSignupCompanyConfirmUuid = ({ onSuccess }: { onSuccess: () => void }) => {
  return useMutation({
    mutationFn: signupCompanyConfirmUuid,
    onSuccess,
  });
};

export const useSetCompanyUserInfo = ({ onSuccess }: { onSuccess: () => void }) => {
  const { t } = useScopedTranslate(locale);

  return useMutation({
    mutationFn: setCompanyUserInfo,
    onSuccess,
    onError: () => {
      handleError(t('signUpFailed'));
    },
  });
};

type InviteUserProps = {
  onSuccess?: () => void;
};
export function useInviteUser(props: InviteUserProps) {
  const { t } = useScopedTranslate(locale);
  const { invalidateUsers } = useInvalidateUsers();

  return useMutation((id: string) => inviteUser(id), {
    onSuccess: () => {
      showSuccess(t('inviteSent'));
      props.onSuccess?.();
    },
    onError: () => {
      handleError(t('userNotInvited'));
    },
    onSettled: () => {
      invalidateUsers();
    },
  });
}

export const useCreateAndInviteUsers = (
  options?: UseMutationOptions<unknown, UserError, UserInvite>
) => {
  const { t } = useScopedTranslate(locale);
  const completeOnboardingStep = useCompleteOnboardingStep();
  const { invalidateUsers } = useInvalidateUsers();

  return useMutation({
    ...options,
    mutationFn: (data: UserInvite) => {
      return createAndInviteUsers(data);
    },
    onSuccess: (...data) => {
      invalidateUsers();
      showSuccess(t('usersInvited'));
      options?.onSuccess?.(...data);
      completeOnboardingStep('inviteTeammate');
    },
    onError: () => {
      handleError(t('usersNotInvited'));
    },
  });
};

export type ResetPassword = {
  email: string;
};

export const useResetPassword = (options?: UseMutationOptions<unknown, unknown, ResetPassword>) => {
  const { t } = useScopedTranslate(locale);

  return useMutation({
    ...options,
    mutationFn: (data) => resetPassword(data),
    onSuccess: (...data) => {
      options?.onSuccess?.(...data);
    },
    onError: () => {
      handleError(t('resetNotSent'));
    },
  });
};

export type UpdatePassword = {
  oldPassword: string;
  password1: string;
  password2: string;
};

type UpdatePasswordError = AxiosError<{ oldPassword: string[] }>;

export const useUpdatePassword = (
  setErrors: (errors: FormStateErrors) => void,
  options?: UseMutationOptions<unknown, UpdatePasswordError, UpdatePassword>
) => {
  const { t } = useScopedTranslate(locale);
  const logout = useLogout();

  return useMutation({
    ...options,
    mutationFn: (data) => updatePassword(data),
    onSuccess: (...data) => {
      showSuccess(t('passwordUpdated'));
      options?.onSuccess?.(...data);
      setTimeout(() => {
        logout.mutate();
      }, 4000);
    },
    onError: (data) => {
      handleError(() => {
        if (data?.response?.data?.oldPassword) {
          setErrors({ oldPassword: data.response.data.oldPassword[0] });
        }
        showError(t('passwordNotUpdated'));
      });
    },
  });
};

const locale = {
  en: {
    userCreated: 'User created',
    userNotCreated: 'User not created',
    userEdited: 'User updated',
    userNotEdited: 'User not updated',
    userDeleted: 'User deleted',
    userNotDeleted: 'User not deleted',
    passwordNotSet: 'Password not set',
    userNotInvited: 'User not invited',
    inviteSent: 'Invite sent',
    resetNotSent: 'Reset email not sent',
    passwordNotUpdated: 'Unable to change password',
    passwordUpdated:
      'Password successfully changed. You will need to log back in with your new password.',
    signUpFailed: 'Unable to sign up',
    resendFailed: 'Unable to resend confirmation email',
    usersInvited: 'Users invited',
    usersNotInvited: 'Users not invited',
  },
};
